export const formatEin = (value) => {
  if (!value) {
    return '';
  }

  const onlyNums = value.replace(/[^\d]/g, '');
  if (onlyNums.length <= 2) {
    return onlyNums;
  }
  return `${onlyNums.slice(0, 2)}-${onlyNums.slice(2)}`;
};

export const formatPhone = (value) => {
  if (!value) {
    return '';
  }

  const onlyNums = value.replace(/[^\d]/g, '');
  if (onlyNums.length <= 3) {
    return onlyNums;
  }
  if (onlyNums.length <= 7) {
    return `${onlyNums.slice(0, 3)}-${onlyNums.slice(3)}`;
  }
  return `(${onlyNums.slice(0, 3)}) ${onlyNums.slice(3, 6)}-${onlyNums.slice(6, 10)}`;
};

export const formatCurrency = (value) => {
  let result = formatNumberAllowNegativesAndDecimal(value);

  if (!result) {
    return '';
  }

  return '$' + result;
};

export const formatPercent = (value) => {
  let result = formatNumberAllowNegativesAndDecimal(value);

  if (!result) {
    return '';
  }

  return result + '%';
};

export const formatNumberAllowNegativesAndDecimal = (value) => {
  if (!value) {
    return '';
  }

  value = value.toString();
  const onlyNums = value.replace(/[^\-\d\.]/g, '');

  if (onlyNums === '-') {
    return '-';
  }

  if (!onlyNums) {
    return '';
  }
  if (onlyNums === '.') {
    return '.';
  }
  if (onlyNums === '-.') {
    return '-.';
  }

  let separator = ',';

  // Determine sign
  let sign = onlyNums.startsWith('-') ? '-' : '';

  // Remove the negative sign for processing
  let absoluteValue = onlyNums.replace(/^-/, '');

  // Split into integer and decimal parts without converting to Number to preserve precision
  let decimalIndex = absoluteValue.indexOf('.');
  let integerPart =
    decimalIndex !== -1
      ? absoluteValue.substring(0, decimalIndex)
      : absoluteValue;
  let decimalPart =
    decimalIndex !== -1 ? absoluteValue.substring(decimalIndex) : '';

  // Remove leading zeros but keep at least one zero
  integerPart = integerPart.replace(/^0+/, '') || '0';

  // Format the integer part with commas
  integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, separator);

  if (decimalPart && decimalPart.length > 1) {
    // Remove trailing zeros: ".500" -> ".5", ".50" -> ".5"
    decimalPart = decimalPart.replace(/0+$/, '');
    // If only decimal point remains, keep it for user feedback during typing
    if (decimalPart === '.') {
      decimalPart = '.';
    }
  }

  // Combine the sign, integer part, and decimal part (if any)
  return sign + integerPart + decimalPart;
};

export const formatNumberAllowNegatives = (value) => {
  if (!value) {
    return '';
  }

  value = value.toString();

  const onlyNums = value.replace(/[^\-\d]/g, '');

  if (onlyNums === '-') {
    return '-';
  }

  let n = Number.parseInt(onlyNums);

  let separator = ',',
    sign = n < 0 ? '-' : '',
    absoluteValue = String(parseInt((n = Math.abs(Number(n) || 0)))),
    j = (j = absoluteValue.length) > 3 ? j % 3 : 0;

  return (
    sign +
    (j ? absoluteValue.substr(0, j) + separator : '') +
    absoluteValue.substr(j).replace(/(\d{3})(?=\d)/g, '$1' + separator)
  );
};

export const formatNumberAllowDecimal = (value) => {
  if (!value) {
    return '';
  }

  value = value.toString();

  const onlyNums = value.replace(/[^\d\.]/g, '');

  if (!onlyNums) {
    return '';
  }

  let separator = ',';

  // Split into integer and decimal parts without converting to Number to preserve precision
  let decimalIndex = onlyNums.indexOf('.');
  let integerPart =
    decimalIndex !== -1 ? onlyNums.substring(0, decimalIndex) : onlyNums;
  let decimalPart = decimalIndex !== -1 ? onlyNums.substring(decimalIndex) : '';

  // Remove leading zeros but keep at least one zero
  integerPart = integerPart.replace(/^0+/, '') || '0';

  // Format the integer part with commas
  integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, separator);

  // Combine the integer part with the decimal part (if any)
  return integerPart + decimalPart;
};

export const formatNumber = (value) => {
  if (!value) {
    return '';
  }

  value = value.toString();

  const onlyNums = value.replace(/[^\d]/g, '');

  let n = Number.parseInt(onlyNums);

  let separator = ',',
    absoluteValue = String(parseInt((n = Math.abs(Number(n) || 0)))),
    j = (j = absoluteValue.length) > 3 ? j % 3 : 0;

  return (
    (j ? absoluteValue.substr(0, j) + separator : '') +
    absoluteValue.substr(j).replace(/(\d{3})(?=\d)/g, '$1' + separator)
  );
};

export const formatDate = (value) => {
  if (!value) {
    return '';
  }

  value = value.toString();
  let lastSlash = value.lastIndexOf('/');

  if (value.length > 10 && lastSlash === -1) {
    try {
      let d = new Date(value);
      if (isNaN(d.getTime())) {
        return value;
      }
      return d.getMonth() + 1 + '/' + d.getDate() + '/' + d.getFullYear();
    } catch (e) {
      return value;
    }
  }

  let onlyDigitsAndSlashes = value.replace(/[^\d\/]/g, '');
  lastSlash = onlyDigitsAndSlashes.lastIndexOf('/');

  if (lastSlash >= 3 && onlyDigitsAndSlashes.length > lastSlash + 4) {
    onlyDigitsAndSlashes = onlyDigitsAndSlashes.substring(0, lastSlash + 5);
  }

  return onlyDigitsAndSlashes;
};

export const formatNumberAllowNegativesAndLessThanZero = (value) => {
  if (!value) {
    return '';
  }

  value = value.toString();

  const onlyNums = value.replace(/[^\-\d\.]/g, '');

  if (onlyNums === '-' || value.startsWith('--')) {
    return '-';
  }

  if (!onlyNums || onlyNums === '.') {
    return '';
  }

  let separator = ',';

  // Determine sign - preserve negative sign for -0 cases
  let sign = onlyNums.startsWith('-') ? '-' : '';

  // Remove the negative sign for processing
  let absoluteValue = onlyNums.replace(/^-/, '');

  // Split into integer and decimal parts without converting to Number to preserve precision
  let decimalIndex = absoluteValue.indexOf('.');
  let integerPart =
    decimalIndex !== -1
      ? absoluteValue.substring(0, decimalIndex)
      : absoluteValue;
  let decimalPart =
    decimalIndex !== -1 ? absoluteValue.substring(decimalIndex) : '';

  // Remove leading zeros but keep at least one zero
  integerPart = integerPart.replace(/^0+/, '') || '0';

  // Format the integer part with commas
  integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, separator);

  // Combine the sign, integer part, and decimal part (if any)
  return sign + integerPart + decimalPart;
};
